import { DatasetType } from "api/model/enums";
import { DatasetChartProps, DatasetSpecification } from "components/dashboard/charts/interfaces";
import {ComponentConfigDefault, TimeConfigDefaultDefined} from "models/dataset-models";
import { any } from "prop-types";
import {ChronoUnitLegacy} from "utils/time-utils";

const objectPropertiesToArray = (object: any) => {
    return Object.keys(object)
        .map((key) => {
            return {
                id: key,
                ...object[key]
            }
        });
}

const getEasasRestUrl = () => {
    return `${window.contextRoot  }/rest`;
}

const pad = (number: number) => {
    let r = String(number);
    if (r.length === 1) {
        r = `0${  r}`;
    }
    return r;
}

const getBuildId = () => {
    return process.env.BUILD_ID;
}

const isProd = () => {
    return process.env.PRODUCTION === "true";
}

const isDev = () => {
    return !isProd();
}

const getEasasJsVersionsScraperUrl = () => {
    return process.env.EASASJS_VERSIONS_SCRAPER_URL!!
}

const isNumeric = (num: any) => (typeof (num) === 'number' || typeof (num) === "string" && num.trim() !== '') && !isNaN(num as number);

const isStringEmpty = (str: string | null | undefined) => !str

const isStringBlank = (str: string | null | undefined) => (!str || str.trim().length === 0)

const timeConfigValidation = (config: ComponentConfigDefault) => {
    const tempConfig: TimeConfigDefaultDefined = {
        alignStartTo: "HOURS" as ChronoUnitLegacy,
        startOffsetUnits: "HOURS" as ChronoUnitLegacy,
        startOffset: 0,
        lagUnits: "HOURS" as ChronoUnitLegacy,
        lagOffset: 0,
        leadUnits: "HOURS" as ChronoUnitLegacy,
        leadOffset: 0
    };

    if (config.alignStartTo) {
        tempConfig.alignStartTo = config.alignStartTo;
    }

    if (config.startOffsetUnits) {
        tempConfig.startOffsetUnits = config.startOffsetUnits;
    }
    if (config.startOffset) {
        tempConfig.startOffset = config.startOffset;
    }

    if (config.lagUnits) {
        tempConfig.lagUnits = config.lagUnits;
    }
    if (config.lagOffset) {
        tempConfig.lagOffset = config.lagOffset;
    }

    if (config.leadUnits) {
        tempConfig.leadUnits = config.leadUnits;
    }
    if (config.leadOffset) {
        tempConfig.leadOffset = config.leadOffset;
    }

    return tempConfig;
}

const getInitials = (name: string) => {
    if (name !== "") {
        const hasTokens = name.includes(' ')
        const firstLetter = name[0].toUpperCase()
        const secondLetter = hasTokens ?
            name.charAt(name.lastIndexOf(' ') + 1).toUpperCase() :
            name.length > 1 ? name[1] :
                ""

        return firstLetter + secondLetter
    }
    return ""
}


const getColorForString = (string: string): { color: string, bgColor: string } => {
    const colors = [
        "#55efc4", "#81ecec", "#74b9ff", "#a29bfe", "#dfe6e9",
        "#00b894", "#00cec9", "#0984e3", "#6c5ce7", "#b2bec3",
        "#ffeaa7", "#fab1a0", "#ff7675", "#fd79a8",
        "#fdcb6e", "#e17055", "#d63031", "#e84393"
    ];

    const hash = Math.abs(hashCode(string));
    const bucket = hash % colors.length;

    return {color: "#ffffff", bgColor: colors[bucket]};
}

export const formatHexColor = (color: string) => {
    return color.startsWith('#') ? color : `#${color}`;
}

const hashCode = (str: string) => str.split('').reduce((s, c) => Math.imul(31, s) + c.charCodeAt(0) | 0, 0)

const queryParam = (name: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
}

type Join<K, P> = K extends string | number ?
    P extends string | number ?
        `${K}${"" extends P ? "" : "."}${P}`
        : never : never;

type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
    11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...0[]]

export type Paths<T, D extends number = 6> = [D] extends [never] ? never : T extends object ?
    {
        [K in keyof T]-?: K extends string | number ?
        `${K}` | Join<K, Paths<T[K], Prev[D]>>
        : never
    }[keyof T] : ""

export type Leaves<T, D extends number = 10> = [D] extends [never] ? never : T extends object ?
    { [K in keyof T]-?: Join<K, Leaves<T[K], Prev[D]>> }[keyof T] : "";


type DotPrefix<T extends string> = T extends "" ? "" : `.${T}`

type DotNestedKeys<T> = (T extends object ?
    {
        [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}`
    } [Exclude<keyof T, symbol>]
    : "") extends infer D ? Extract<D, string> : never;


const setObjAttribute = (obj: any, path: string, value: any,) => {
    let i;
    const pathArr = path.split('.');
    for (i = 0; i < pathArr.length - 1; i++)
        obj = obj[pathArr[i]];

    obj[pathArr[i]] = value;
}

/**
 * Get attribute from object by path. If attribute is not found, or path is not valid, return default value
 * @param obj
 * @param path
 * @param rDefault
 */
const getObjAttribute = (obj: any, path: any, rDefault: any = null) => {
    if(!obj) {
        return rDefault
    }

    let i;
    const pathArr = path.split('.');
    for (i = 0; i < pathArr.length - 1; i++)
        obj = obj[pathArr[i]];

    return obj[pathArr[i]] || rDefault;
}

const standardDeviation = (numArray: number[], averageCalculated?: number):number|undefined => {
    const arrLength = numArray.length;
    if(arrLength<=1) {
        return undefined
    }

    const mean = averageCalculated || numArray.reduce((s, n) => s + n) / arrLength;
    const variance = numArray.reduce((s, n) => s + (n - mean) ** 2, 0) / (arrLength - 1);
    return Math.sqrt(variance);
}

const checkIfArraysLengthIsSame= (...arrays:Array<any>) => {
    const definedArrays = arrays.filter((arr) => arr !== undefined);

    if (definedArrays.length === 0) {
        return true; // All arrays are undefined, so they are considered to have the same length
    }

    const firstArrayLength = definedArrays[0].length;

    return definedArrays.every((arr) => arr.length === firstArrayLength);
}

const extractNumber = (value: string | number | undefined): number => {
    if (!value) {
        return 0
    }
    if (typeof value === "string") {
        const parsedNumber = parseFloat(value);
        if (!isNaN(parsedNumber)) {
            return parsedNumber;
        } 
            return 400;
        
    }

    return value
}

const stringArrayToObject = (array:string[]) => {
    const obj:any = {};
    array.forEach((key) => {
        obj[key] = undefined;
    });
    return obj;
}

const isJSON = (str: string | undefined) => {
    try {
        if (typeof str === "string") {
            JSON.parse(str);
            return true;
        }
        return false;
    } catch (e) {
        return false;
    }
}

const isParametersValid = (parametersData: any, selectedParameters: any) => {
    const message = { isValid: true, details: "" };

    if (!parametersData.parameters || parametersData.parameters.length === 0) {
        message.isValid = false;
        message.details = 'Need at least one parameter';
    } else if (selectedParameters.length > parametersData.parameters.length) {
        message.isValid = false;
        message.details = 'All parameters need SP or Min/Max set for them';
    } else if (parametersData.parameters.some((parameter: any) => parameter.sp === null && parameter.min === null && parameter.max === null)) {
        message.isValid = false;
        message.details = 'All parameters need SP or Min/Max set for them';
    }

    return message;
};

const findValue = (arr: Array<any>, targetTime: number) => {
    let left = 0;
    let right = arr.length - 1;

    while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const tempDate = new Date(arr[mid].key);
        tempDate.setMilliseconds(0);

        if (tempDate.getTime() === targetTime) {
            return arr[mid].value;
        } if (tempDate.getTime() < targetTime) {
            left = mid + 1;
        } else {
            right = mid - 1;
        }
    }

    return null;
}

const isDatasetIdentifier = (value: string) => {
    const idRegex = /^(\d+):([A-Z_]+)$/;
    if (!idRegex.test(value)) return false;

    const parts = value.split(':');
    if (parts.length !== 2) return false;

    const datasetType = parts[1] as keyof typeof DatasetType;
    if (DatasetType[datasetType] === undefined) return false;

    return true;
};

const isObjectEmpty = (obj: any) => {
    return Object.keys(obj).length === 0;
}

const addLeadingZeros = (num: number, targetLength: number) => {
    return String(num).padStart(targetLength, '0');
  }
const handleRowReorder = (data: Array<Object> | undefined, setCurrentConfig: (config: any) => void, currentConfig?: DatasetChartProps) => (event: any) => {
    let orderedValues = [...data || []];
    let draggedValue = orderedValues[event.dragIndex];
    orderedValues.splice(event.dragIndex, 1);
    orderedValues.splice(event.dropIndex, 0, draggedValue);
    currentConfig ? setCurrentConfig({...currentConfig, datasets: orderedValues}): setCurrentConfig(orderedValues);
};

export {
  objectPropertiesToArray,
  getEasasRestUrl,
  pad,
  getBuildId,
  isProd,
  isDev,
  getEasasJsVersionsScraperUrl,
  isNumeric,
  isStringEmpty,
  isStringBlank,
  timeConfigValidation,
  getInitials,
  getColorForString,
  queryParam,
  setObjAttribute,
  getObjAttribute,
  standardDeviation,
  checkIfArraysLengthIsSame,
  extractNumber,
  stringArrayToObject,
  isJSON,
  isParametersValid,
  findValue,
  isDatasetIdentifier,
  isObjectEmpty,
  addLeadingZeros,
  handleRowReorder
};